import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { ErrorMessageComponent, InfoMessageComponent, AutoFocusDirective } from '@tailormap-viewer/shared';

@Component({
    selector: 'tm-password-reset-request-form',
    templateUrl: './password-reset-request-form.component.html',
    styleUrls: ['./password-reset-request-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        MatIcon,
        ErrorMessageComponent,
        InfoMessageComponent,
        AutoFocusDirective,
        MatButton,
        MatProgressSpinner,
        RouterLink,
        AsyncPipe,
    ],
})
export class PasswordResetRequestFormComponent {
  @Input() public requestReset$!: (email: string) => Observable<boolean>;
  @Output() public backToLogin = new EventEmitter<void>();
  // eslint-disable-next-line max-len
  private acceptedMessage = $localize`:@@shared.password-reset-request-form.accepted-message:Your request was accepted. If the email you provided is registered, you will receive an email with instructions to reset your password.`;
  private errorMessage = $localize`:@@shared.password-reset-request-form.error-message:An error occurred processing your request.`;
  private formBuilder = inject(FormBuilder);
  public requestResetForm = this.formBuilder.group({
    email: [ '', [ Validators.required, Validators.email ]],
  });
  private processingSubject = new BehaviorSubject(false);
  public processing$ = this.processingSubject.asObservable();
  private errorMessageSubject = new BehaviorSubject('');
  public errorMessage$ = this.errorMessageSubject.asObservable();
  private infoMessageSubject = new BehaviorSubject('');
  public infoMessage$ = this.infoMessageSubject.asObservable();

  public requestReset() {
    const email = this.requestResetForm.get('email')?.value;
    if (!email || !this.requestReset$) {
      return;
    }
    this.processingSubject.next(true);
    this.requestReset$(email)
      .pipe(take(1))
      .subscribe((success: boolean) => {
        if (success) {
          this.infoMessageSubject.next(this.acceptedMessage);
        } else {
          this.errorMessageSubject.next(this.errorMessage);
        }
        this.processingSubject.next(false);
        this.requestResetForm.reset();
      });
  }

  public returnToLogin(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.backToLogin.emit();
  }

}
